import { FC } from 'react';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { mainColors } from '../../../../styling/theme';
import type { FundPerformanceAnalysisPayload } from '../../../../api/rs-hg/risk/fundPerformanceAnalysis.types';
import { percentageToTwoDecimalPlacesNoHundred } from '../../../../utilities/numberFormatters';
import GenericLineChart from '../../../charts/GenericLineChart';
import GeneralCard from '../../../general/GeneralCard';
import { Typography } from '@mui/material';

export interface HistoricalPerformanceChartProps {
  payload: FundPerformanceAnalysisPayload;
}

function buildHistroicalPerformanceData(payload: FundPerformanceAnalysisPayload) {
  const returnsData = payload.returns_data;
  if (!returnsData?.length) return [];

  const headers = returnsData[0];
  const numIndices = headers.length - 1;
  const chartData: Record<string, string | number>[] = [];

  returnsData.slice(1).forEach((value) => {
    const date = value[0];
    const currentDateData: Record<string, string | number> = {
      date: String(date),
    };
    for (let i = 1; i <= numIndices; i++) {
      currentDateData[`index${i}`] = Number(value[i]);
    }
    chartData.push(currentDateData);
  });

  return chartData;
}

function getIndexNames(payload: FundPerformanceAnalysisPayload): string[] {
  const returnsData = payload.returns_data;
  if (!returnsData?.length) return [];

  return returnsData[0].slice(1).map(String);
}

const HistoricalPerformanceChart: FC<HistoricalPerformanceChartProps> = (
  props,
) => {
  const chartData = buildHistroicalPerformanceData(props.payload);
  const numIndices = chartData.length
    ? Object.keys(chartData[0]).length - 1
    : 0;
  const indexNames = getIndexNames(props.payload);
  // Use as a circular array in case where we have more lines than colors
  const colors = [
    mainColors.mainBlue_lighter,
    mainColors.Pass,
    mainColors.Fail,
    mainColors.Alert,
    mainColors.fourthPurple,
    mainColors.pineGreen,
  ];
  const lines = Array.from(Array(numIndices + 1).keys())
    .slice(1)
    .map((item: any) => {
      const n = colors.length;
      return {
        dataKey: `index${item}`,
        color: colors[(((item - 1) % n) + n) % n],
      };
    });
  const { width, height } = useWindowSize();
  return (
    <GeneralCard cardStyle={{ marginBottom: '1.00rem' }}>
      <Typography
        variant="h2"
        style={{
          marginLeft: '1.25rem',
          marginTop: '0.62rem',
          fontSize: '1.25rem',
          fontWeight: 400,
        }}
      >
        Historical Performance Data
      </Typography>
      <GenericLineChart
        title="Historical Performance Data"
        data={chartData}
        export
        id="historical_performance_data"
        xAxisDataKey="date"
        yAxisTickFormatter={(value: number) =>
          percentageToTwoDecimalPlacesNoHundred(value)
        }
        tooltipFormatter={(value: number, name: string) => {
          return [
            percentageToTwoDecimalPlacesNoHundred(value),
            indexNames[parseInt(name.replace('index', '')) - 1],
          ];
        }}
        yAxisLabel={{ value: 'Return (%)', angle: -90, position: 'insideLeft' }}
        yAxisWidth={80}
        lines={lines}
        width={'100%'}
        height={height / 2 - 100}
        showDot={false}
        margin={{
          top: 10,
          bottom: 10,
          left: 20,
          right: 50,
        }}
        error={'No Historical Performance Data Available'}
        pdfDontmoveToNewPage
        pdfSpecificYPosition={25}
        pdfSpecificXPosition={5}
      />
    </GeneralCard>
  );
};

export default HistoricalPerformanceChart;
